import {objectFromEntries} from '@benchling/js-utils';
import produce from 'immer';

import {BlobDetail, NoteReference} from 'notebook/ELNConstants';
import {DocumentExportActionType} from 'notebook/entries/ExportActions';
import {ChipState} from 'notebook/mediocre/core-ui/SnapshotChip';
import {BenchlingAction} from 'redux-stores/Action';
import {apiIdToIconName} from 'util/APIUtil';

/**
 * This Redux state contains all of the information that we need to render an Export beyond what is contained
 * within the Document. This allows us to preload some information so we can do things like load SnapshotChips
 * instead of SmartChips, which are lighter weight and don't require any additional network fetch.
 */
export interface DocumentsExportState {
  [documentApiId: string]: DocumentExportState;
}

interface DocumentExportState {
  chips: {
    [documentApiId: string]: ChipState;
  };
}

function isNoteReference(item: unknown): item is NoteReference {
  return (
    typeof item === 'object' && item != null && item.hasOwnProperty('webURL') && item.hasOwnProperty('id')
  );
}

function makeChipState(item: NoteReference | BlobDetail): ChipState {
  if (isNoteReference(item)) {
    const {
      webURL,
      id,
      name,
      isArchived,
      archivePurpose,
      secondaryText,
      isInaccessible,
      schemaValidationStatus,
      tagSchemaIconId,
      sequenceType,
    } = item;
    return {
      href: webURL,
      icon: tagSchemaIconId ?? apiIdToIconName(id),
      text: name ?? '',
      isArchived,
      archivePurpose,
      secondaryText,
      isInaccessible,
      schemaValidationStatus,
      ...(tagSchemaIconId !== undefined ? {tagSchemaIconId} : {}),
      ...(sequenceType !== undefined ? {sequenceType} : {}),
    };
  }
  const {name, previewFileUrl, isPDFPreviewFileMissing} = item;
  return {text: name ?? '', previewFileUrl, isPDFPreviewFileMissing};
}

const DOCUMENT_EXPORT_DEFAULT_STATE = {};

const reduceDocumentExportState = produce((draft, action) => {
  switch (action.type) {
    case DocumentExportActionType.LOAD_NOTE_REFERENCES: {
      draft[action.fileId] = {
        chips: objectFromEntries([
          ...Object.entries(draft[action.fileId]?.chips ?? {}).map(([id, chipState]) => [id, chipState]),
          ...action.noteReferences.map((noteReference: NoteReference) => [
            noteReference.id,
            makeChipState(noteReference),
          ]),
        ]),
      };
      break;
    }
    case DocumentExportActionType.LOAD_BLOB_DETAILS: {
      draft[action.fileId] = {
        chips: objectFromEntries([
          ...Object.entries(draft[action.fileId]?.chips ?? {}).map(([id, chipState]) => [id, chipState]),
          ...action.blobDetails.map((blobDetail: BlobDetail) => [blobDetail.id, makeChipState(blobDetail)]),
        ]),
      };
      break;
    }

    default:
      if (draft == null) {
        draft = DOCUMENT_EXPORT_DEFAULT_STATE;
      }
  }
});

export default function reduceDocumentExport(
  state: DocumentsExportState = DOCUMENT_EXPORT_DEFAULT_STATE,
  action: BenchlingAction
): DocumentsExportState {
  return reduceDocumentExportState(state, action);
}
